// MÜŞTERİ PANELİ — AKTİVİTE ↔ HİZMET KARTI EŞLEŞMESİ (saf mantık, tarayıcı API'si kullanmaz).
//
// NEDEN AYRI DOSYA: `js/views/client-os.ts` yüklenirken `window`a yazıyor ve bu yüzden birim
// testinde import EDİLEMİYOR (ölçüldü 11.10.2026: "window is not defined"). Eşleşme kuralı o
// dosyanın içindeyken HİÇ ölçülmemişti — zincirin son halkası ölçüsüz kalmıştı.
//
// ÖLÇÜLEN KUSUR (11.10.2026): sunucu tarafı artık `service:` beyanını okuyor ve çok hizmetli bir
// raporu her beyan edilen hizmet için bir satır olarak gönderiyor
// (`notify/lib/client-os-enrichment.ts` → `hizmetleriCoz` + `cogalt`). Ekranın v2 kartı ise
// beyanı YOK SAYIP başlık/özet metninden tahmin yürütüyordu:
//   · `typeOf(a.service)` anahtarı yalnız google/meta/web türüne indiriyordu;
//   · `mervaks` ↔ `mervakspro` ayrımını BAŞLIK METNİNDE arıyordu;
//   · `usedIdx` ile her kayıt YALNIZ BİR karta giriyordu → iki hizmet beyan eden raporun ikinci
//     hizmet kartı o işi hiç göstermiyordu.
// Yani açıkça belirlenmiş hizmet ilişkisi, son adımda metin tahminine geri düşüyordu.
//
// KURAL SIRASI (beyan önce, tahmin sonra):
//   1. `a.service === svc.key` → KESİN EŞLEŞME. Kart bu işi gösterir. Anahtar tekil olduğu için
//      aynı satır başka karta gidemez; "tek karta" tahsisi (usedIdx) burada GEREKMEZ.
//   2. `a.service` kataloğun BAŞKA bir hizmetinin anahtarı → bu karta GİRMEZ. Beyan başka kartı
//      işaret ediyorsa metin tahminine düşmek işi YANLIŞ KARTA yazar.
//   3. `a.service` kataloğda hiç yok (eski kayıt; `google-ads` gibi genel anahtar) → eskiden beri
//      çalışan tahmin yolu AYNEN uygulanır: kanal türü + mervaks/pro metin ayrımı + tek kart
//      tahsisi. Geriye uyum bu maddeyle korunur.
//
// Katalog bilinmiyorsa (boş `tumAnahtarlar`) 2. madde uygulanamaz; o durumda beyanı tanınmamış
// sayıp tahmin yoluna düşmek, bugünkü davranışı korur.

// İKİNCİ KUSUR (11.10.2026, aynı ölçüm): hizmet KARTI için çoğaltma doğrudur — her kart kendi
// hizmetinin satırını gösterir. Ama hizmetten BAĞIMSIZ listeler (ör. "Yaklaşan Adımlar") aynı
// `activity` dizisini doğrudan okuyor; çok hizmet beyan eden bir iş orada İKİ KEZ çıkıyor ve
// listenin sınırlı yerini (slice) iki kez yiyordu. `benzersizIsler` o listeler için satırları
// işe indirir ve hizmetlerini tek satırda toplar. Hizmet kartları bu fonksiyonu KULLANMAZ.

/** Hizmet kartının eşleşme için gereken alanları (`ServiceDef`'in alt kümesi). */
export interface EslesmeHizmeti {
  key: string;
  domain?: string;
  channels?: string[];
}

/** Aktivitenin eşleşme için gereken alanları (`ServiceActivity`'nin alt kümesi). */
export interface EslesmeAktivitesi {
  service: string;
  title: string;
  summary?: string;
}

/** Kanal listesinden kartın kabul ettiği tür kümesi. */
function istenenTurler(channels: readonly string[]): Set<string> {
  const w = new Set<string>();
  if (channels.some((c) => c.startsWith('google'))) w.add('google');
  if (channels.includes('meta')) w.add('meta');
  if (channels.includes('web')) w.add('web');
  return w;
}

/** Hizmet anahtarından tür TAHMİNİ — yalnız 3. maddede (beyan tanınmıyorsa) kullanılır. */
function turTahmini(s: string): 'google' | 'meta' | 'web' {
  if (/google|gads/.test(s)) return 'google';
  if (/meta|sosyal/.test(s)) return 'meta';
  return 'web';
}

/**
 * Tek bir aktivitenin bu karta girip girmediğini söyler.
 *
 * @param svc Kart.
 * @param akt Aktivite satırı.
 * @param tumAnahtarlar Kiracının bütün hizmet anahtarları (`services[].key`). Beyanın BAŞKA bir
 *   kartı işaret ettiğini anlamak için gerekir; boşsa 2. madde uygulanmaz.
 * @returns `kesin`: beyan bu kartın anahtarıdır (tek-kart tahsisi gerekmez).
 *   `tahmin`: beyan tanınmıyor, eski tür/metin yolu kabul etti (tek-kart tahsisi UYGULANIR).
 *   `hayir`: girmez.
 */
export function aktiviteKartaGirer(
  svc: EslesmeHizmeti,
  akt: EslesmeAktivitesi,
  tumAnahtarlar: readonly string[] = [],
): 'kesin' | 'tahmin' | 'hayir' {
  const beyan = (akt.service || '').trim();
  // 1. Kesin eşleşme — beyan bu kartın anahtarı.
  if (beyan && beyan === svc.key) return 'kesin';
  // 2. Beyan kataloğun BAŞKA bir hizmetini işaret ediyor → tahmine düşmek yanlış kart üretir.
  if (beyan && tumAnahtarlar.includes(beyan)) return 'hayir';

  // 3. Beyan tanınmıyor → eski tahmin yolu (geriye uyum).
  const wants = istenenTurler(svc.channels || []);
  if (!wants.has(turTahmini(beyan))) return 'hayir';
  const txt = (akt.title + ' ' + (akt.summary || '')).toLowerCase();
  const proGecer = txt.includes('mervakspro') || txt.includes('mervaks pro');
  const domTok = (svc.domain || '').split('.')[0].toLowerCase();
  if (domTok === 'mervakspro' && wants.has('google') && !proGecer) return 'hayir';
  if (domTok === 'mervaks' && wants.has('google') && proGecer) return 'hayir';
  return 'tahmin';
}

/**
 * Bir kartın göstereceği aktiviteleri seçer.
 *
 * `kullanilanIndeks` SADECE tahmin yolundaki kayıtlar için işaretlenir: aynı belirsiz kayıt iki
 * Google kartında tekrarlanmasın (eski davranış). Beyanı KESİN olan kayıt bu kümeye girmez —
 * çoğaltılmış satırların her biri kendi kartına gitmelidir.
 */
export function kartAktiviteleri<T extends EslesmeAktivitesi>(
  svc: EslesmeHizmeti,
  aktiviteler: readonly T[],
  kullanilanIndeks: Set<number>,
  tumAnahtarlar: readonly string[] = [],
): T[] {
  const out: T[] = [];
  aktiviteler.forEach((a, i) => {
    const karar = aktiviteKartaGirer(svc, a, tumAnahtarlar);
    if (karar === 'hayir') return;
    if (karar === 'tahmin') {
      if (kullanilanIndeks.has(i)) return;
      kullanilanIndeks.add(i);
    }
    out.push(a);
  });
  return out;
}

/** Çoğaltılmış satırların ardındaki İŞ kimliği — sunucu aynı kaydı her hizmet için tekrarlar. */
function isKimligi(a: EslesmeAktivitesi & { date?: string }): string {
  return [a.title || '', a.date || '', a.summary || ''].join('\u0000');
}

/**
 * Hizmetten BAĞIMSIZ listeler için satırları işe indirir.
 *
 * Aynı iş (başlık + tarih + özet) için tek satır döner; satırın `hizmetler` alanı o işin beyan
 * edilen bütün hizmetlerini SIRASINI KORUYARAK taşır. Sıra girdi sırasıdır — çağıran kendi
 * sıralamasını zaten uygulamıştır.
 *
 * Toplam sayımı bu fonksiyonun sonucundan yapılmalıdır: `aktiviteler.length` çoğaltılmış satır
 * sayısıdır, iş sayısı DEĞİLDİR.
 */
export function benzersizIsler<T extends EslesmeAktivitesi & { date?: string }>(
  aktiviteler: readonly T[],
): (T & { hizmetler: string[] })[] {
  const sira: (T & { hizmetler: string[] })[] = [];
  const indeks = new Map<string, T & { hizmetler: string[] }>();
  for (const a of aktiviteler) {
    const k = isKimligi(a);
    const mevcut = indeks.get(k);
    if (mevcut) {
      // Aynı hizmet aynı iş içinde tekrar beyan edilmişse İKİNCİ KEZ yazılmaz.
      if (a.service && !mevcut.hizmetler.includes(a.service)) mevcut.hizmetler.push(a.service);
      continue;
    }
    const yeni = { ...a, hizmetler: a.service ? [a.service] : [] };
    indeks.set(k, yeni);
    sira.push(yeni);
  }
  return sira;
}
